import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cases, CASE_TYPES, type CaseType, type CaseItem } from '../../mocks/cases';
import CasesSEOHead from './CasesSEOHead';
import { useLang } from '../../hooks/useLang';

const CASES_LABELS: Record<string, Record<string, string>> = {
  title: { zh: '全球 87 个 RWA 案例库', en: 'Global 87 RWA Case Library', fr: 'Bibliothèque de 87 Cas RWA Mondiaux', es: 'Biblioteca de 87 Casos RWA Globales', pt: 'Biblioteca de 87 Casos RWA Globais', ja: 'グローバル87件RWA事例ライブラリ', de: 'Globale 87 RWA-Fallbibliothek', ko: '글로벌 87개 RWA 사례 라이브러리', ar: 'مكتبة 87 حالة RWA عالمية', ru: 'Глобальная Библиотека 87 Случаев RWA' },
  subtitle: { zh: '覆盖美国、欧盟、香港、新加坡等主要市场，涵盖7大资产赛道，含智能合约源码、法律结构及合规路径完整解析。持续更新。', en: 'Covering major markets including the US, EU, Hong Kong, and Singapore, spanning 7 asset tracks, with complete analysis of smart contract source code, legal structures, and compliance pathways. Continuously updated.', fr: 'Couvrant les principaux marchés dont les États-Unis, l\'UE, Hong Kong et Singapour, couvrant 7 pistes d\'actifs, avec une analyse complète du code source des contrats intelligents, des structures juridiques et des voies de conformité.', es: 'Cubriendo los principales mercados incluyendo EE.UU., UE, Hong Kong y Singapur, abarcando 7 pistas de activos, con análisis completo del código fuente de contratos inteligentes, estructuras legales y vías de cumplimiento.', pt: 'Cobrindo os principais mercados incluindo EUA, UE, Hong Kong e Singapura, abrangendo 7 trilhas de ativos, com análise completa do código-fonte de contratos inteligentes, estruturas legais e caminhos de conformidade.', ja: '米国、EU、香港、シンガポールなどの主要市場をカバーし、7つの資産トラックにわたり、スマートコントラクトのソースコード、法的構造、コンプライアンス経路の完全な分析を含む。継続的に更新。', de: 'Abdeckung wichtiger Märkte einschließlich USA, EU, Hongkong und Singapur, umfasst 7 Asset-Tracks, mit vollständiger Analyse von Smart-Contract-Quellcode, Rechtsstrukturen und Compliance-Wegen.', ko: '미국, EU, 홍콩, 싱가포르 등 주요 시장을 포함하여 7개 자산 트랙에 걸쳐 스마트 계약 소스 코드, 법적 구조 및 컴플라이언스 경로의 완전한 분석을 포함합니다.', ar: 'يغطي الأسواق الرئيسية بما في ذلك الولايات المتحدة والاتحاد الأوروبي وهونغ كونغ وسنغافورة، ويمتد عبر 7 مسارات أصول، مع تحليل كامل لكود العقود الذكية والهياكل القانونية ومسارات الامتثال.', ru: 'Охватывает основные рынки, включая США, ЕС, Гонконг и Сингапур, охватывает 7 треков активов, с полным анализом исходного кода смарт-контрактов, правовых структур и путей соответствия.' },
  totalCases: { zh: '总案例', en: 'Total Cases', fr: 'Cas Totaux', es: 'Casos Totales', pt: 'Casos Totais', ja: '総事例', de: 'Gesamtfälle', ko: '총 사례', ar: 'إجمالي الحالات', ru: 'Всего Случаев' },
  active: { zh: '运营中', en: 'Active', fr: 'Actifs', es: 'Activos', pt: 'Ativos', ja: '運営中', de: 'Aktiv', ko: '운영 중', ar: 'نشط', ru: 'Активных' },
  regions: { zh: '覆盖地区', en: 'Regions', fr: 'Régions', es: 'Regiones', pt: 'Regiões', ja: '地域', de: 'Regionen', ko: '지역', ar: 'مناطق', ru: 'Регионов' },
  tracks: { zh: '资产赛道', en: 'Asset Tracks', fr: 'Pistes d\'Actifs', es: 'Pistas de Activos', pt: 'Trilhas de Ativos', ja: '資産トラック', de: 'Asset-Tracks', ko: '자산 트랙', ar: 'مسارات الأصول', ru: 'Треков Активов' },
  searchPlaceholder: { zh: '搜索案例、机构、地区...', en: 'Search cases, institutions, regions...', fr: 'Rechercher cas, institutions, régions...', es: 'Buscar casos, instituciones, regiones...', pt: 'Pesquisar casos, instituições, regiões...', ja: 'ケース、機関、地域を検索...', de: 'Fälle, Institutionen, Regionen suchen...', ko: '사례, 기관, 지역 검색...', ar: 'البحث في الحالات والمؤسسات والمناطق...', ru: 'Поиск случаев, институтов, регионов...' },
  found: { zh: '找到', en: 'Found', fr: 'Trouvé', es: 'Encontrado', pt: 'Encontrado', ja: '見つかりました', de: 'Gefunden', ko: '찾음', ar: 'وجد', ru: 'Найдено' },
  cases: { zh: '个案例', en: 'cases', fr: 'cas', es: 'casos', pt: 'casos', ja: '件の事例', de: 'Fälle', ko: '개 사례', ar: 'حالة', ru: 'случаев' },
  category: { zh: '分类', en: 'Category', fr: 'Catégorie', es: 'Categoría', pt: 'Categoria', ja: 'カテゴリ', de: 'Kategorie', ko: '카테고리', ar: 'فئة', ru: 'Категория' },
  keyword: { zh: '关键词', en: 'Keyword', fr: 'Mot-clé', es: 'Palabra clave', pt: 'Palavra-chave', ja: 'キーワード', de: 'Schlüsselwort', ko: '키워드', ar: 'كلمة مفتاحية', ru: 'Ключевое слово' },
  dataUpdate: { zh: '数据更新：2026.03', en: 'Data updated: 2026.03', fr: 'Données mises à jour: 2026.03', es: 'Datos actualizados: 2026.03', pt: 'Dados atualizados: 2026.03', ja: 'データ更新: 2026.03', de: 'Daten aktualisiert: 2026.03', ko: '데이터 업데이트: 2026.03', ar: 'تحديث البيانات: 2026.03', ru: 'Данные обновлены: 2026.03' },
  home: { zh: '首页', en: 'Home', fr: 'Accueil', es: 'Inicio', pt: 'Início', ja: 'ホーム', de: 'Startseite', ko: '홈', ar: 'الرئيسية', ru: 'Главная' },
  caseLib: { zh: '案例深度库', en: 'Case Library', fr: 'Bibliothèque de Cas', es: 'Biblioteca de Casos', pt: 'Biblioteca de Casos', ja: '事例ライブラリ', de: 'Fallbibliothek', ko: '사례 라이브러리', ar: 'مكتبة الحالات', ru: 'Библиотека Случаев' },
};

function getCL(key: string, lang: string): string {
  return CASES_LABELS[key]?.[lang] ?? CASES_LABELS[key]?.['en'] ?? key;
}

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  债券:      { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200', dot: 'bg-violet-500'  },
  房地产:    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  大宗商品:  { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',  dot: 'bg-amber-500'   },
  股权:      { bg: 'bg-pink-50',    text: 'text-pink-700',    border: 'border-pink-200',   dot: 'bg-pink-500'    },
  碳信用:    { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200',   dot: 'bg-teal-500'    },
  供应链信贷:{ bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200', dot: 'bg-orange-500'  },
  其他:      { bg: 'bg-slate-50',   text: 'text-slate-600',   border: 'border-slate-200',  dot: 'bg-slate-400'   },
};

const STATUS_COLORS: Record<string, { dot: string; text: string }> = {
  运营中: { dot: 'bg-emerald-400', text: 'text-emerald-700' },
  试点:   { dot: 'bg-violet-400',  text: 'text-violet-700'  },
  已发行: { dot: 'bg-amber-400',   text: 'text-amber-700'   },
  规划中: { dot: 'bg-slate-400',   text: 'text-slate-600'   },
};

const TYPE_STATS = ['债券', '房地产', '大宗商品', '股权', '碳信用', '供应链信贷', '其他'];

// Case type translations
const TYPE_TRANSLATIONS: Record<string, Record<string, string>> = {
  '全部': { en: 'All', fr: 'Tout', es: 'Todo', pt: 'Tudo', ja: 'すべて', de: 'Alle', ko: '전체', ar: 'الكل', ru: 'Все' },
  '债券': { en: 'Bonds', fr: 'Obligations', es: 'Bonos', pt: 'Títulos', ja: '債券', de: 'Anleihen', ko: '채권', ar: 'سندات', ru: 'Облигации' },
  '房地产': { en: 'Real Estate', fr: 'Immobilier', es: 'Bienes Raíces', pt: 'Imóveis', ja: '不動産', de: 'Immobilien', ko: '부동산', ar: 'عقارات', ru: 'Недвижимость' },
  '大宗商品': { en: 'Commodities', fr: 'Matières Premières', es: 'Materias Primas', pt: 'Commodities', ja: '商品', de: 'Rohstoffe', ko: '원자재', ar: 'سلع', ru: 'Сырьё' },
  '股权': { en: 'Equity', fr: 'Actions', es: 'Acciones', pt: 'Ações', ja: '株式', de: 'Aktien', ko: '주식', ar: 'أسهم', ru: 'Акции' },
  '碳信用': { en: 'Carbon Credits', fr: 'Crédits Carbone', es: 'Créditos de Carbono', pt: 'Créditos de Carbono', ja: 'カーボンクレジット', de: 'CO₂-Zertifikate', ko: '탄소 크레딧', ar: 'ائتمانات الكربون', ru: 'Углеродные Кредиты' },
  '供应链信贷': { en: 'Supply Chain Credit', fr: 'Crédit Chaîne d\'Approvisionnement', es: 'Crédito de Cadena de Suministro', pt: 'Crédito de Cadeia de Suprimentos', ja: 'サプライチェーン信用', de: 'Lieferketten-Kredit', ko: '공급망 신용', ar: 'ائتمان سلسلة التوريد', ru: 'Кредит Цепочки Поставок' },
  '其他': { en: 'Other', fr: 'Autre', es: 'Otro', pt: 'Outro', ja: 'その他', de: 'Sonstige', ko: '기타', ar: 'أخرى', ru: 'Другое' },
};

function getTypeLabel(type: string, lang: string): string {
  if (lang === 'zh') return type;
  return TYPE_TRANSLATIONS[type]?.[lang] ?? TYPE_TRANSLATIONS[type]?.['en'] ?? type;
}

// Status translations
const STATUS_TRANSLATIONS: Record<string, Record<string, string>> = {
  '运营中': { en: 'Active', fr: 'Actif', es: 'Activo', pt: 'Ativo', ja: '運営中', de: 'Aktiv', ko: '운영 중', ar: 'نشط', ru: 'Активен' },
  '试点': { en: 'Pilot', fr: 'Pilote', es: 'Piloto', pt: 'Piloto', ja: 'パイロット', de: 'Pilotprojekt', ko: '시범', ar: 'تجريبي', ru: 'Пилот' },
  '已发行': { en: 'Issued', fr: 'Émis', es: 'Emitido', pt: 'Emitido', ja: '発行済み', de: 'Ausgegeben', ko: '발행됨', ar: 'صادر', ru: 'Выпущен' },
  '规划中': { en: 'Planned', fr: 'Planifié', es: 'Planificado', pt: 'Planejado', ja: '計画中', de: 'Geplant', ko: '계획 중', ar: 'مخطط', ru: 'Запланирован' },
};

export default function CasesPage() {
  const { lang } = useLang();
  const [activeType, setActiveType] = useState<CaseType>('全部');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const perPage = 12;

  const filtered = useMemo(() => cases.filter((c) => {
    const matchType = activeType === '全部' || c.type === activeType;
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || c.desc.includes(q) || c.region.includes(q) || c.platform.toLowerCase().includes(q) || (c.issuer || '').includes(q);
    return matchType && matchSearch;
  }), [activeType, search]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleFilter = (t: CaseType) => { setActiveType(t); setPage(1); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  return (
    <div className="min-h-screen bg-[#F8F7FF]">
      <CasesSEOHead />
      {/* Header */}
      <div className="bg-gradient-to-br from-[#3D1F8A] via-[#5B2EBF] to-[#7C3AED] pt-16 pb-12 px-4 lg:px-8">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-home-line text-xs"></i> {getCL('home', lang)}
            </Link>
            <i className="ri-arrow-right-s-line text-white/40 text-xs"></i>
            <span className="text-white text-sm">{getCL('caseLib', lang)}</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-white mb-3">
                {getCL('title', lang).replace('87', '')} <span className="text-[#F59E0B]">87</span> {lang !== 'zh' ? '' : '个 RWA 案例库'}
              </h1>
              <p className="text-white/70 max-w-2xl text-base leading-relaxed">
                {getCL('subtitle', lang)}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { v: '87', l: getCL('totalCases', lang) }, { v: '63', l: getCL('active', lang) },
                { v: '12+', l: getCL('regions', lang) }, { v: '7', l: getCL('tracks', lang) },
              ].map((s) => (
                <div key={s.l} className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-center min-w-[70px]">
                  <div className="text-white font-black text-xl leading-none">{s.v}</div>
                  <div className="text-white/60 text-xs mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Category breakdown bar */}
          <div className="mt-8 flex flex-wrap gap-2">
            {TYPE_STATS.map((typeKey) => {
              const count = cases.filter((c) => c.type === typeKey).length;
              const c = TYPE_COLORS[typeKey];
              return (
                <button
                  key={typeKey}
                  onClick={() => handleFilter(typeKey as CaseType)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    activeType === typeKey ? 'bg-white text-[#5B2EBF]' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
                  {getTypeLabel(typeKey, lang)} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between mb-6">
          <div className="flex flex-wrap gap-2">
            {CASE_TYPES.map((typeKey) => (
              <button
                key={typeKey}
                onClick={() => handleFilter(typeKey)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeType === typeKey
                    ? 'bg-[#7C3AED] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#7C3AED]/40 hover:text-[#7C3AED]'
                }`}
              >
                {getTypeLabel(typeKey, lang)}
                {typeKey === '全部' ? ` (${cases.length})` : ` (${cases.filter((c) => c.type === typeKey).length})`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={getCL('searchPlaceholder', lang)}
                className="border border-gray-200 rounded-full px-4 py-2 pl-9 text-sm w-52 bg-white focus:outline-none focus:border-[#7C3AED] transition-colors"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              {search && (
                <button onClick={() => handleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1">
              <button onClick={() => setViewMode('grid')} className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors ${viewMode === 'grid' ? 'bg-[#7C3AED] text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                <i className="ri-grid-line text-sm"></i>
              </button>
              <button onClick={() => setViewMode('list')} className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors ${viewMode === 'list' ? 'bg-[#7C3AED] text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                <i className="ri-list-check text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-500 mb-5">
          {getCL('found', lang)} <span className="text-[#7C3AED] font-bold">{filtered.length}</span> {getCL('cases', lang)}
          {activeType !== '全部' && <span className="ml-1">· {getCL('category', lang)}：<strong>{activeType}</strong></span>}
          {search && <span className="ml-1">· {getCL('keyword', lang)}：<strong>{search}</strong></span>}
        </div>

        {/* Grid / List view */}
        {viewMode === 'grid' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {paginated.map((c) => <CaseCard key={c.id} item={c} />)}
          </div>
        ) : (
          <div className="space-y-2 mb-8">
            {paginated.map((c) => <CaseListItem key={c.id} item={c} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-[#7C3AED] hover:text-[#7C3AED] disabled:opacity-40 cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-base"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium cursor-pointer transition-all ${p === page ? 'bg-[#7C3AED] text-white' : 'border border-gray-200 bg-white text-gray-600 hover:border-[#7C3AED]'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-[#7C3AED] hover:text-[#7C3AED] disabled:opacity-40 cursor-pointer transition-colors">
              <i className="ri-arrow-right-s-line text-base"></i>
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-gray-400">
          <span className="text-[#7C3AED] font-semibold">87</span> {getCL('cases', lang)} · 7 {getCL('tracks', lang)} · {getCL('dataUpdate', lang)}
        </div>
      </div>
    </div>
  );
}

function CaseCard({ item }: { item: CaseItem }) {
  const { lang } = useLang();
  const tc = TYPE_COLORS[item.type] || TYPE_COLORS['其他'];
  const sc = STATUS_COLORS[item.status] || STATUS_COLORS['运营中'];
  const hasContract = !!item.contractCode;
  const typeLabel = getTypeLabel(item.type, lang);
  const statusLabel = STATUS_TRANSLATIONS[item.status]?.[lang] ?? STATUS_TRANSLATIONS[item.status]?.['en'] ?? item.status;
  const sourceCodeLabel = { zh: '源码', en: 'Source', fr: 'Code', es: 'Código', pt: 'Código', ja: 'ソース', de: 'Quelle', ko: '소스', ar: 'كود', ru: 'Код' };
  return (
    <Link to={`/cases/${item.id}`}
      className="bg-white border border-gray-100 rounded-xl p-4 hover:border-[#7C3AED]/40 hover:-translate-y-0.5 transition-all cursor-pointer group block">
      <div className="flex items-start justify-between mb-2.5">
        <div className="flex flex-wrap gap-1">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${tc.bg} ${tc.text} ${tc.border}`}>{typeLabel}</span>
          {hasContract && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#3D1F8A]/8 text-[#3D1F8A] border border-[#3D1F8A]/15 flex items-center gap-1">
              <i className="ri-code-s-slash-line text-xs"></i>{sourceCodeLabel[lang as keyof typeof sourceCodeLabel] ?? sourceCodeLabel['en']}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-1">
          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
          <span className={`text-xs ${sc.text} font-medium`}>{statusLabel}</span>
        </div>
      </div>
      <h4 className="font-bold text-gray-800 text-sm leading-snug mb-1.5 group-hover:text-[#7C3AED] transition-colors line-clamp-2">{item.name}</h4>
      <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{item.desc}</p>
      <div className="flex items-center justify-between pt-2.5 border-t border-gray-50">
        <span className="font-bold text-[#7C3AED] text-xs flex items-center gap-1">
          <i className="ri-bar-chart-2-line text-xs"></i>{item.tvl}
        </span>
        <div className="flex items-center gap-2 text-gray-400 text-xs">
          <span className="flex items-center gap-1"><i className="ri-map-pin-line text-xs"></i>{item.region}</span>
          <span className="flex items-center gap-1"><i className="ri-links-line text-xs"></i>{item.platform.split('/')[0]}</span>
        </div>
      </div>
    </Link>
  );
}

function CaseListItem({ item }: { item: CaseItem }) {
  const { lang } = useLang();
  const tc = TYPE_COLORS[item.type] || TYPE_COLORS['其他'];
  const sc = STATUS_COLORS[item.status] || STATUS_COLORS['运营中'];
  const hasContract = !!item.contractCode;
  const typeLabel = getTypeLabel(item.type, lang);
  const statusLabel = STATUS_TRANSLATIONS[item.status]?.[lang] ?? STATUS_TRANSLATIONS[item.status]?.['en'] ?? item.status;
  const sourceTooltip = { zh: '含智能合约源码', en: 'Includes smart contract source code', fr: 'Inclut le code source du contrat', es: 'Incluye código fuente del contrato', pt: 'Inclui código fonte do contrato', ja: 'スマートコントラクトソースコード付き', de: 'Enthält Smart-Contract-Quellcode', ko: '스마트 계약 소스 코드 포함', ar: 'يتضمن كود العقد الذكي', ru: 'Включает исходный код смарт-контракта' };
  return (
    <Link to={`/cases/${item.id}`}
      className="bg-white border border-gray-100 rounded-xl px-5 py-4 flex items-center gap-4 hover:border-[#7C3AED]/40 transition-all cursor-pointer group block">
      <div className="w-8 h-8 flex items-center justify-center bg-[#7C3AED]/8 rounded-full text-[#7C3AED] font-black text-xs shrink-0">{item.id}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${tc.bg} ${tc.text} ${tc.border} shrink-0`}>{typeLabel}</span>
          <h4 className="font-bold text-gray-800 text-sm truncate group-hover:text-[#7C3AED] transition-colors">{item.name}</h4>
          {hasContract && <i className="ri-code-s-slash-line text-[#3D1F8A] text-sm shrink-0" title={sourceTooltip[lang as keyof typeof sourceTooltip] ?? sourceTooltip['en']}></i>}
        </div>
        <p className="text-gray-500 text-xs truncate">{item.desc}</p>
      </div>
      <div className="hidden md:flex items-center gap-4 text-xs text-gray-500 shrink-0">
        <span>{item.region}</span>
        <span className="font-bold text-[#7C3AED]">{item.tvl}</span>
        <div className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
          <span className={sc.text}>{statusLabel}</span>
        </div>
        <i className="ri-arrow-right-s-line text-gray-400"></i>
      </div>
    </Link>
  );
}
